import React, { useEffect, useState } from "react";
import { Alert, Box, Button, CircularProgress, Dialog, DialogContent, Typography } from "@mui/material";
import { completeAudit, unwrapPost } from "../../../../helpers/auditHub_helper";
import { AH_ACCENT, AH_ACCENT_HOVER, AH_TEXT, AH_WHITE, auditHubPageSx } from "../theme";

const UNDECIDED_CODE = "AUDIT_HAS_UNDECIDED_CONTROLS";

const dialogButtonSx = {
  minWidth: 140,
  height: 38,
  px: 2,
  borderRadius: "4px",
  textTransform: "none",
  fontWeight: 400,
  fontSize: "0.875rem",
  boxShadow: "none",
  "&:hover": { boxShadow: "none" },
};

const CompleteAuditDialog = ({ audit, open, onClose, onCompleted }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [undecided, setUndecided] = useState("");

  useEffect(() => {
    if (!open) return;
    setSaving(false);
    setError("");
    setUndecided("");
  }, [open, audit?.auditId]);

  const confirm = async () => {
    if (!audit?.auditId || saving) return;
    const force = Boolean(undecided);
    setSaving(true);
    setError("");
    try {
      unwrapPost(await completeAudit(audit.auditId, { force }));
      onCompleted(audit);
    } catch (err) {
      if (!force && err?.data?.error?.code === UNDECIDED_CODE) {
        setUndecided(err.message || "Some manual controls in this assignment are still undecided.");
      } else {
        setError(err?.message || "Unable to complete this assignment.");
      }
    } finally {
      setSaving(false);
    }
  };

  const label = [audit?.auditCode, audit?.name].filter(Boolean).join(" · ");
  const total = Number(audit?.progress?.total);
  const decided = Number(audit?.progress?.decided) || 0;
  const hasProgress = Number.isFinite(total) && total > 0;

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="complete-audit-title"
      PaperProps={{ sx: { ...auditHubPageSx, borderRadius: "12px", overflow: "hidden", maxWidth: 600 } }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          minHeight: 56,
          px: 3,
          backgroundColor: AH_ACCENT,
        }}
      >
        <Typography id="complete-audit-title" sx={{ fontWeight: 700, fontSize: "1.0625rem", color: AH_WHITE, lineHeight: 1.3 }}>
          Complete Assignment
        </Typography>
        <Box
          component="button"
          type="button"
          aria-label="Close"
          disabled={saving}
          onClick={onClose}
          sx={{
            p: 0,
            m: 0,
            border: "none",
            background: "transparent",
            color: AH_WHITE,
            fontSize: 28,
            fontWeight: 700,
            lineHeight: 1,
            cursor: saving ? "default" : "pointer",
          }}
        >
          ×
        </Box>
      </Box>
      <DialogContent sx={{ px: 3, py: 2.5, backgroundColor: AH_WHITE }}>
        <Typography sx={{ fontSize: "0.875rem", color: AH_TEXT, lineHeight: 1.6, overflowWrap: "anywhere" }}>
          Are you sure you want to complete{" "}
          {label ? (
            <Box component="span" sx={{ fontWeight: 600 }}>
              {label}
            </Box>
          ) : (
            "this assignment"
          )}
          ?
        </Typography>
        {hasProgress ? (
          <Typography sx={{ fontSize: "0.8125rem", color: AH_TEXT, lineHeight: 1.6, mt: 1 }}>
            <Box component="span" sx={{ fontWeight: 600 }}>
              {decided} of {total}
            </Box>{" "}
            manual controls are decided.
          </Typography>
        ) : null}
        <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", lineHeight: 1.6, mt: 1 }}>
          Completing freezes the assignment permanently and stops all of its scans. It cannot be reopened; a repeat assignment is a new record.
        </Typography>
        {undecided ? (
          <Alert severity="warning" sx={{ mt: 2, borderRadius: "8px", fontSize: "0.8125rem", alignItems: "center" }}>
            {undecided} Select <strong>Complete anyway</strong> to close it with those controls undecided.
          </Alert>
        ) : null}
      </DialogContent>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: { xs: "wrap", sm: "nowrap" },
          gap: 1.5,
          px: 3,
          py: 2,
          borderTop: "1px solid #e5e7eb",
          backgroundColor: "#f8fafc",
        }}
      >
        <Button
          onClick={onClose}
          disabled={saving}
          sx={{
            ...dialogButtonSx,
            backgroundColor: "#74788d",
            color: "#eff2f7",
            "&:hover": { backgroundColor: "#636678", boxShadow: "none" },
          }}
        >
          Close
        </Button>
        {error ? (
          <Typography
            role="alert"
            sx={{
              flex: 1,
              minWidth: 0,
              order: { xs: 3, sm: 0 },
              flexBasis: { xs: "100%", sm: "auto" },
              fontSize: "0.8125rem",
              lineHeight: 1.45,
              color: "#dc2626",
              textAlign: "center",
              overflowWrap: "anywhere",
            }}
          >
            {error}
          </Typography>
        ) : null}
        <Button
          onClick={confirm}
          disabled={saving || !audit?.auditId}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{
            ...dialogButtonSx,
            backgroundColor: AH_ACCENT,
            color: AH_WHITE,
            "&:hover": { backgroundColor: AH_ACCENT_HOVER, boxShadow: "none" },
            "&.Mui-disabled": { backgroundColor: AH_ACCENT, color: AH_WHITE, opacity: 0.65 },
          }}
        >
          {saving ? "Completing" : undecided ? "Complete anyway" : "Complete"}
        </Button>
      </Box>
    </Dialog>
  );
};

export default CompleteAuditDialog;
